/* eslint-disable react-hooks/exhaustive-deps */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import {
  Check, X, SkipForward, Loader2, Beaker, ArrowLeft, ArrowRight, ChevronDown, ChevronUp, Sparkles, AlertTriangle, Camera, FileCheck,
} from 'lucide-react';
import { tl } from '../lib/testingLabApi';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import AkroChatPanel from '../components/testing_lab/AkroChatPanel';
import ScreenCaptureButton from '../components/testing_lab/ScreenCaptureButton';
import LiveTranscriptionButton from '../components/testing_lab/LiveTranscriptionButton';

const STATUS_COLORS = {
  pending: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
  passed: 'bg-emerald-500 text-white',
  failed: 'bg-red-500 text-white',
  skipped: 'bg-amber-500 text-white',
};

const TestingLabRunnerPage = () => {
  const { session_id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [cases, setCases] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCaseId, setActiveCaseId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showAkro, setShowAkro] = useState(false);
  const [showSignOff, setShowSignOff] = useState(false);
  const [isAdmin, setIsAdmin] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const sess = await tl.getSession(session_id);
      setSession(sess);
      if (sess.test_suite_id) {
        const c = await tl.listCases(sess.test_suite_id);
        setCases(c.items || []);
        if (!activeCaseId && c.items?.[0]) setActiveCaseId(c.items[0].test_case_id);
      }
      const r = await tl.listStepRuns(session_id);
      setRuns(r.items || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    tl.superAdminMe().then((d) => setIsAdmin(!!d?.is_super_admin)).catch(() => setIsAdmin(false));
  }, []);

  useEffect(() => { if (session_id && isAdmin) load(); }, [session_id, isAdmin]);

  // Ensure step_runs exist for the active case
  useEffect(() => {
    if (!activeCaseId || !session_id) return;
    const existingForCase = runs.filter((r) => r.test_case_id === activeCaseId);
    if (existingForCase.length === 0) {
      (async () => {
        try {
          await tl.startCaseInSession(session_id, activeCaseId);
          await load();
        } catch (e) { toast.error(e.message); }
      })();
    }
  }, [activeCaseId, runs.length]);

  const activeCase = cases.find((c) => c.test_case_id === activeCaseId);
  const activeRuns = useMemo(
    () => runs.filter((r) => r.test_case_id === activeCaseId).sort((a, b) => a.order - b.order),
    [runs, activeCaseId]
  );

  const stats = useMemo(() => {
    const total = activeRuns.length;
    const passed = activeRuns.filter((r) => r.status === 'passed').length;
    const failed = activeRuns.filter((r) => r.status === 'failed').length;
    const skipped = activeRuns.filter((r) => r.status === 'skipped').length;
    const complete = passed + failed + skipped;
    return { total, passed, failed, skipped, complete, pct: total ? Math.round(complete / total * 100) : 0 };
  }, [activeRuns]);

  const mark = async (runId, status, comment) => {
    setBusy(true);
    // Optimistic
    setRuns((prev) => prev.map((r) => (r.step_run_id === runId ? { ...r, status, comment: comment ?? r.comment } : r)));
    try {
      await tl.updateStepRun(runId, { status, ...(comment !== undefined ? { comment } : {}) });
      // Refetch cases to get updated case status
      if (session?.test_suite_id) {
        const c = await tl.listCases(session.test_suite_id);
        setCases(c.items || []);
      }
    } catch (e) {
      toast.error(e.message);
      load();
    } finally { setBusy(false); }
  };

  const saveComment = async (runId, comment) => {
    try {
      await tl.updateStepRun(runId, { comment });
      setRuns((prev) => prev.map((r) => (r.step_run_id === runId ? { ...r, comment } : r)));
    } catch (e) { toast.error(e.message); }
  };

  const goNextCase = () => {
    const idx = cases.findIndex((c) => c.test_case_id === activeCaseId);
    if (idx >= 0 && idx < cases.length - 1) setActiveCaseId(cases[idx + 1].test_case_id);
  };
  const goPrevCase = () => {
    const idx = cases.findIndex((c) => c.test_case_id === activeCaseId);
    if (idx > 0) setActiveCaseId(cases[idx - 1].test_case_id);
  };

  if (isAdmin === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="p-8 text-center max-w-md">
          <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold">Super Admin required</h2>
          <p className="text-sm text-muted-foreground mt-1">This runner is only for Testing Lab Super Admins.</p>
          <Button className="mt-4" onClick={() => navigate('/dashboard')}>Go to dashboard</Button>
        </Card>
      </div>
    );
  }

  if (loading || !session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="runner-page">
      {/* Sticky header */}
      <header className="sticky top-0 z-10 bg-card border-b border-border px-4 py-2.5">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
            <Beaker className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-semibold truncate">{session.title}</h1>
            <p className="text-[11px] text-muted-foreground truncate">
              {session.browser_context?.route && <span>{session.browser_context.route} · </span>}
              {cases.length} case{cases.length !== 1 ? 's' : ''}
              {session.browser_context?.planned_by === 'akro' && (
                <Badge className="ml-2 bg-violet-500 text-white text-[9px] h-3.5 px-1"><Sparkles className="w-2.5 h-2.5 mr-0.5 inline" /> Akro-planned</Badge>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm" variant="outline"
              onClick={() => setShowSignOff(true)}
              disabled={session.signed_off_at}
              data-testid="runner-sign-off"
            >
              <FileCheck className="w-3 h-3 mr-1" />
              {session.signed_off_at ? 'Signed off' : 'Sign off'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowAkro(!showAkro)} data-testid="runner-toggle-akro">
              <Sparkles className="w-3 h-3 mr-1" /> {showAkro ? 'Hide' : 'Ask'} Akro
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate(`/testing-lab?session=${session_id}&tab=sessions`)} data-testid="runner-full-lab">
              Open in Testing Lab
            </Button>
          </div>
        </div>
      </header>

      <div className={cn('flex-1 grid gap-3 p-3', showAkro ? 'grid-cols-1 lg:grid-cols-[260px_1fr_340px]' : 'grid-cols-1 lg:grid-cols-[260px_1fr]')}>
        {/* Case sidebar */}
        <Card className="p-2 flex flex-col">
          <div className="px-1 py-1.5 border-b border-border mb-1.5">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Test cases</h3>
          </div>
          <ScrollArea className="flex-1">
            <div className="space-y-1">
              {cases.map((c, idx) => {
                const caseRuns = runs.filter((r) => r.test_case_id === c.test_case_id);
                const done = caseRuns.filter((r) => r.status !== 'pending').length;
                const failed = caseRuns.filter((r) => r.status === 'failed').length;
                return (
                  <button key={c.test_case_id}
                    onClick={() => setActiveCaseId(c.test_case_id)}
                    className={cn(
                      'w-full text-left p-2 rounded text-xs border transition-colors',
                      activeCaseId === c.test_case_id
                        ? 'border-violet-500 bg-violet-500/5'
                        : 'border-border hover:border-violet-500/50'
                    )}
                    data-testid={`runner-case-${c.test_case_id}`}>
                    <div className="flex items-center gap-1 mb-0.5">
                      <span className="text-[9px] text-muted-foreground">#{idx + 1}</span>
                      <Badge variant="outline" className="text-[9px] h-3.5 px-1">{c.priority}</Badge>
                      {c.status === 'passed' && <Badge className="bg-emerald-500 text-white text-[9px] h-3.5 px-1"><Check className="w-2.5 h-2.5" /></Badge>}
                      {c.status === 'failed' && <Badge className="bg-red-500 text-white text-[9px] h-3.5 px-1"><X className="w-2.5 h-2.5" /></Badge>}
                    </div>
                    <div className="font-medium line-clamp-2">{c.title}</div>
                    {caseRuns.length > 0 && (
                      <div className="flex items-center gap-1 mt-1 text-[9px] text-muted-foreground">
                        <span>{done}/{caseRuns.length}</span>
                        {failed > 0 && <span className="text-red-500 font-semibold">· {failed} fail</span>}
                      </div>
                    )}
                  </button>
                );
              })}
              {cases.length === 0 && (
                <div className="text-center py-8 text-xs text-muted-foreground">No cases in this session</div>
              )}
            </div>
          </ScrollArea>
        </Card>

        {/* Active case runner */}
        <Card className="p-4 flex flex-col overflow-hidden">
          {!activeCase ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
              Select a test case
            </div>
          ) : (
            <>
              <div className="mb-3">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="text-[10px]">{activeCase.test_type}</Badge>
                  <Badge className={cn('text-[10px]', activeCase.priority === 'P0' ? 'bg-red-500' : 'bg-amber-500', 'text-white')}>{activeCase.priority}</Badge>
                  {activeCase.status === 'passed' && <Badge className="bg-emerald-500 text-white text-[10px]"><Check className="w-3 h-3 mr-0.5" /> Passed</Badge>}
                  {activeCase.status === 'failed' && <Badge className="bg-red-500 text-white text-[10px]"><X className="w-3 h-3 mr-0.5" /> Failed</Badge>}
                </div>
                <h2 className="text-lg font-bold" data-testid="runner-active-case-title">{activeCase.title}</h2>
                {activeCase.expected_outcome && (
                  <p className="text-xs text-muted-foreground mt-0.5">Expected: {activeCase.expected_outcome}</p>
                )}
              </div>

              {/* Must / Should lists */}
              {(activeCase.must_work?.length > 0 || activeCase.should_work?.length > 0) && (
                <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                  {activeCase.must_work?.length > 0 && (
                    <div className="border border-red-500/30 bg-red-500/5 rounded p-2">
                      <div className="text-[10px] uppercase font-semibold text-red-600 dark:text-red-400 mb-1">Must work</div>
                      <ul className="space-y-0.5">
                        {activeCase.must_work.map((m, i) => <li key={i} className="text-[11px]">• {m}</li>)}
                      </ul>
                    </div>
                  )}
                  {activeCase.should_work?.length > 0 && (
                    <div className="border border-amber-500/30 bg-amber-500/5 rounded p-2">
                      <div className="text-[10px] uppercase font-semibold text-amber-600 dark:text-amber-400 mb-1">Should work</div>
                      <ul className="space-y-0.5">
                        {activeCase.should_work.map((m, i) => <li key={i} className="text-[11px]">• {m}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Progress bar */}
              <div className="mb-3 space-y-1">
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-muted-foreground">Progress</span>
                  <span className="font-medium">
                    {stats.complete}/{stats.total}
                    {stats.passed > 0 && <span className="text-emerald-600 ml-2">✓ {stats.passed}</span>}
                    {stats.failed > 0 && <span className="text-red-600 ml-2">✗ {stats.failed}</span>}
                    {stats.skipped > 0 && <span className="text-amber-600 ml-2">» {stats.skipped}</span>}
                  </span>
                </div>
                <Progress value={stats.pct} className="h-1.5" />
              </div>

              {/* Steps */}
              <ScrollArea className="flex-1 -mx-2 px-2">
                <div className="space-y-2 pb-2">
                  {activeRuns.map((run, idx) => (
                    <StepCard
                      key={run.step_run_id}
                      run={run}
                      index={idx}
                      busy={busy}
                      onMark={mark}
                      onComment={saveComment}
                      sessionId={session_id}
                    />
                  ))}
                  {activeRuns.length === 0 && (
                    <div className="text-center py-8 text-xs text-muted-foreground">
                      Initializing steps…
                    </div>
                  )}
                </div>
              </ScrollArea>

              <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
                <Button size="sm" variant="outline" onClick={goPrevCase}
                  disabled={cases.findIndex((c) => c.test_case_id === activeCaseId) === 0}
                  data-testid="runner-prev-case">
                  <ArrowLeft className="w-3 h-3 mr-1" /> Previous case
                </Button>
                <Button size="sm" onClick={goNextCase}
                  disabled={cases.findIndex((c) => c.test_case_id === activeCaseId) >= cases.length - 1}
                  data-testid="runner-next-case">
                  Next case <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </>
          )}
        </Card>

        {/* Akro side panel */}
        {showAkro && (
          <AkroChatPanel testSessionId={session_id} />
        )}
      </div>

      <SignOffDialog
        open={showSignOff}
        onClose={() => setShowSignOff(false)}
        sessionId={session_id}
        runs={runs}
        onSigned={(updated) => { setSession(updated); setShowSignOff(false); }}
      />
    </div>
  );
};

const StepCard = ({ run, index, busy, onMark, onComment, sessionId }) => {
  const [expanded, setExpanded] = useState(run.status === 'pending');
  const [comment, setComment] = useState(run.comment || '');

  useEffect(() => { setComment(run.comment || ''); }, [run.comment]);

  const statusColor = STATUS_COLORS[run.status] || STATUS_COLORS.pending;

  const onTranscript = (transcript) => {
    const newComment = comment ? `${comment}\n${transcript}` : transcript;
    setComment(newComment);
    onComment(run.step_run_id, newComment);
  };

  return (
    <div className={cn(
      'border rounded-lg transition-colors',
      run.status === 'passed' ? 'border-emerald-500/30 bg-emerald-500/5' :
      run.status === 'failed' ? 'border-red-500/30 bg-red-500/5' :
      run.status === 'skipped' ? 'border-amber-500/30 bg-amber-500/5' :
      'border-border'
    )} data-testid={`runner-step-${run.step_run_id}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-3 py-2 flex items-center gap-2 text-left"
        data-testid={`runner-step-toggle-${run.step_run_id}`}
      >
        <span className="w-6 h-6 rounded-full border-2 border-border flex items-center justify-center text-xs font-bold flex-shrink-0">
          {index + 1}
        </span>
        <Badge className={cn('text-[10px]', statusColor)}>{run.status}</Badge>
        <span className="flex-1 text-sm font-medium">{run.instruction}</span>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {expanded && (
        <div className="px-3 pb-3 space-y-2 border-t border-border/50">
          {run.expected_result && (
            <div className="pt-2">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Expected</div>
              <div className="text-xs mt-0.5">{run.expected_result}</div>
            </div>
          )}
          <div className="grid grid-cols-3 gap-2">
            <Button
              size="sm"
              className={cn('h-10', run.status === 'passed' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-emerald-500 hover:bg-emerald-600')}
              onClick={() => onMark(run.step_run_id, 'passed')}
              disabled={busy}
              data-testid={`step-pass-${run.step_run_id}`}
            >
              <Check className="w-4 h-4 mr-1" /> Pass
            </Button>
            <Button
              size="sm"
              className={cn('h-10', run.status === 'failed' ? 'bg-red-600 hover:bg-red-700' : 'bg-red-500 hover:bg-red-600')}
              onClick={() => onMark(run.step_run_id, 'failed')}
              disabled={busy}
              data-testid={`step-fail-${run.step_run_id}`}
            >
              <X className="w-4 h-4 mr-1" /> Fail
            </Button>
            <Button
              size="sm" variant="outline" className="h-10"
              onClick={() => onMark(run.step_run_id, 'skipped')}
              disabled={busy}
              data-testid={`step-skip-${run.step_run_id}`}
            >
              <SkipForward className="w-4 h-4 mr-1" /> Skip
            </Button>
          </div>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onBlur={() => onComment(run.step_run_id, comment)}
            placeholder="Comment / observation (saved on blur). Use mic to dictate."
            className="min-h-[50px] text-xs"
            data-testid={`step-comment-${run.step_run_id}`}
          />
          <div className="flex items-center gap-2 flex-wrap">
            <ScreenCaptureButton
              sessionId={sessionId}
              stepId={run.step_id}
              onCaptured={() => toast.success('Attached to step')}
              label="Capture screen"
            />
            <LiveTranscriptionButton
              sessionId={sessionId}
              stepId={run.step_id}
              onTranscript={onTranscript}
              label="Talk"
            />
            {(run.evidence_ids || []).length > 0 && (
              <Badge variant="outline" className="text-[10px]">{run.evidence_ids.length} evidence</Badge>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const SignOffDialog = ({ open, onClose, sessionId, runs, onSigned }) => {
  const [verdict, setVerdict] = useState('pass');
  const [summary, setSummary] = useState('');
  const [busy, setBusy] = useState(false);
  const pending = runs.filter((r) => r.status === 'pending').length;
  const passed = runs.filter((r) => r.status === 'passed').length;
  const failed = runs.filter((r) => r.status === 'failed').length;
  const skipped = runs.filter((r) => r.status === 'skipped').length;

  const submit = async () => {
    setBusy(true);
    try {
      const updated = await tl.signOffSession(sessionId, { verdict, summary: summary || null });
      toast.success('Session signed off');
      onSigned && onSigned(updated);
    } catch (e) {
      const msg = typeof e.detail === 'object' && e.detail.error === 'pending_steps'
        ? `${e.detail.pending_count} step(s) still pending — Pass/Fail/Skip every step first.`
        : e.message;
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="sign-off-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileCheck className="w-4 h-4" /> Sign off session
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-4 gap-2 text-center">
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-2">
              <div className="text-lg font-bold text-emerald-600">{passed}</div>
              <div className="text-[9px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400">Passed</div>
            </div>
            <div className="bg-red-500/10 border border-red-500/30 rounded p-2">
              <div className="text-lg font-bold text-red-600">{failed}</div>
              <div className="text-[9px] uppercase tracking-wider text-red-700 dark:text-red-400">Failed</div>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded p-2">
              <div className="text-lg font-bold text-amber-600">{skipped}</div>
              <div className="text-[9px] uppercase tracking-wider text-amber-700 dark:text-amber-400">Skipped</div>
            </div>
            <div className={cn('rounded p-2 border', pending > 0 ? 'bg-slate-500/10 border-slate-500/30' : 'bg-emerald-500/10 border-emerald-500/30')}>
              <div className={cn('text-lg font-bold', pending > 0 ? 'text-slate-600' : 'text-emerald-600')}>{pending}</div>
              <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Pending</div>
            </div>
          </div>

          {pending > 0 && (
            <div className="border border-amber-500/30 bg-amber-500/5 rounded p-2 text-xs text-amber-700 dark:text-amber-400">
              <AlertTriangle className="w-3 h-3 inline mr-1" /> {pending} step(s) still pending — sign-off will be blocked until you mark every step.
            </div>
          )}

          <div>
            <div className="text-[10px] uppercase font-semibold text-muted-foreground mb-1.5">Verdict</div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: 'pass', label: 'Pass', color: 'bg-emerald-500 hover:bg-emerald-600' },
                { id: 'pass_with_notes', label: 'Pass with notes', color: 'bg-amber-500 hover:bg-amber-600' },
                { id: 'fail', label: 'Fail', color: 'bg-red-500 hover:bg-red-600' },
              ].map((v) => (
                <Button key={v.id} size="sm"
                  className={cn(verdict === v.id ? v.color + ' text-white' : 'bg-background text-foreground border border-border hover:bg-accent')}
                  onClick={() => setVerdict(v.id)}
                  data-testid={`signoff-verdict-${v.id}`}>
                  {v.label}
                </Button>
              ))}
            </div>
          </div>

          <Textarea
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="Sign-off summary (what was validated, anything to flag)"
            className="min-h-[80px] text-xs"
            data-testid="signoff-summary-input"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="signoff-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || pending > 0} data-testid="signoff-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileCheck className="w-4 h-4 mr-1" />}
            Sign off
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TestingLabRunnerPage;